// Skins: how a colour reaches every part of a device.
//
// A skin is one body colour. Each device has a painter that spreads it over
// the model's materials by what they are: the shell takes the colour, the
// buttons a shade beside it, the wells and seams a darker one, and legends
// and marks a tone that reads against the shell. A member's own colour goes
// through the same painter, so any colour gives a whole device, not a shell
// with another device's buttons.
//
// Materials are matched by name prefix. A material no rule names keeps its
// authored colour: lenses, lights, metal, and the coloured face buttons of
// the 3DS. A skin with no paint is the model as its author made it.

import * as THREE from "three";
import { CUSTOM_SKIN, parseColor } from "../core/catalog.ts";
import type { DeviceId } from "../core/types.ts";

interface Rule {
  match: string[];
  color: string;
  roughness?: number;
  metalness?: number;
}

function mix(a: string, b: string, t: number): string {
  // Display-space blend: these are paint colours picked by eye.
  const x = new THREE.Color(a).convertLinearToSRGB();
  const y = new THREE.Color(b).convertLinearToSRGB();
  return `#${x.lerp(y, t).convertSRGBToLinear().getHexString()}`;
}

/** Perceived lightness of a colour, 0 black to 1 white. */
export function lightness(color: string): number {
  const { r, g, b } = new THREE.Color(color);
  return Math.cbrt(0.2126 * r + 0.7152 * g + 0.0722 * b);
}

/** The tones a body colour implies for the parts around it. */
function tones(body: string) {
  const light = lightness(body) > 0.62;
  return {
    light,
    /** Buttons and pads: the body's colour, a step apart so their shape reads. */
    button: light ? mix(body, "#ffffff", 0.3) : mix(body, "#ffffff", 0.08),
    /** Sockets, seams and the gaps around buttons. */
    well: light ? mix(body, "#1b1b24", 0.26) : mix(body, "#000000", 0.32),
    /** Legends, arrows and printed marks. */
    ink: light ? mix(body, "#1b1b24", 0.62) : mix(body, "#ffffff", 0.74),
    /** Rubber: stick caps and pads. */
    soft: light ? mix(body, "#7c8088", 0.3) : mix(body, "#000000", 0.3),
  };
}

/**
 * A part of a model that shares a material with something painted
 * differently: the model's author merged them. The presenter splits the part
 * back out at load by where each triangle lies and gives it `name` as its own
 * material. `where` gets a triangle's centre in the device's frame:
 * millimetres, X right, Y up, Z out of the screen.
 */
export interface Part {
  material: string;
  name: string;
  where(centre: THREE.Vector3): boolean;
}

/** Where a PCH-2000's sticks stand, mirrored left and right, and how far their wells reach: millimetres in the device's frame. */
const STICK = { x: 69.5, y: -9.5, well: 10 };

export const PARTS: Record<DeviceId, Part[]> = {
  psp: [],
  // One dark material covers the black border of the LCD, the ports and the
  // wells the D-pad, the buttons and the sticks sit in. The wells of the
  // D-pad and the buttons, on the face outside the glass, follow the body's
  // colour. The two sticks' wells do not: the base a stick stands in is black
  // on a PCH-2000 of every colour. A triangle is the first part's that claims it.
  vita: [
    { material: "Deep recess", name: "PCH-2000 stick wells", where: (centre) => centre.z > 5 && Math.hypot(Math.abs(centre.x) - STICK.x, centre.y - STICK.y) < STICK.well },
    { material: "Deep recess", name: "PCH-2000 control wells", where: (centre) => centre.z > 5 && Math.abs(centre.x) > 60 },
  ],
  "3ds": [],
  "ipod-touch": [],
  android: [],
};

type Painter = (body: string) => Rule[];

const PAINTERS: Record<DeviceId, Painter> = {
  psp(body) {
    const t = tones(body);
    return [
      { match: ["Carcasa"], color: body, roughness: t.light ? 0.3 : 0.2 },
      // `tintado` is the D-pad and the four face buttons, `Botones` the row
      // under the screen.
      { match: ["tintado", "Botones"], color: t.button, roughness: 0.32 },
      // The analog nub.
      { match: ["hard1"], color: t.soft },
      { match: ["P3D_decals_and_text__"], color: t.ink },
    ];
  },
  vita(body) {
    const t = tones(body);
    return [
      { match: ["PCH-2000 charcoal ABS", "PCH-2000 front resin"], color: body },
      { match: ["PCH-2000 polished buttons", "PCH-2000 translucent shoulder resin"], color: t.button },
      { match: ["PCH-2000 silver legends", "Warm grey markings"], color: t.ink },
      // The dishes the D-pad and the buttons sit in are the shell's own
      // colour on a PCH-2000, a touch deeper here so their shape reads. The
      // stick caps ("PCH-2000 textured stick rubber") and the wells they
      // stand in have no rule: they are black on the Glacier White, Aqua
      // Blue and Neon Orange consoles as on the black one, and so on any
      // colour here.
      { match: ["PCH-2000 control wells"], color: t.light ? mix(body, "#1b1b24", 0.1) : mix(body, "#000000", 0.16) },
      { match: ["Assembly shadow"], color: t.well },
      { match: ["PCH-2000 rear touch panel"], color: mix(body, "#000000", t.light ? 0.06 : 0.18) },
      { match: ["PCH-2000 touch pattern"], color: mix(body, t.light ? "#000000" : "#ffffff", 0.14) },
    ];
  },
  "3ds"(body) {
    const t = tones(body);
    return [
      { match: ["KTR warm white ABS"], color: body },
      { match: ["KTR white removable plates"], color: mix(body, "#ffffff", t.light ? 0.12 : 0.05) },
      { match: ["KTR bezel highlight"], color: mix(body, t.light ? "#000000" : "#ffffff", 0.09) },
      { match: ["KTR seam interior", "Assembly shadow"], color: t.well },
      { match: ["KTR grey elastomer"], color: t.soft },
      { match: ["Warm grey markings"], color: t.ink },
    ];
  },
  "ipod-touch"(body) {
    const t = tones(body);
    return [
      { match: ["Touch bezel"], color: body },
      // The back is anodised in the same colour.
      { match: ["Touch back"], color: body, roughness: 0.34, metalness: 0.7 },
      { match: ["Touch home dish"], color: mix(body, t.light ? "#000000" : "#ffffff", 0.05) },
      { match: ["Touch home edge", "Touch camera ring"], color: t.well },
      { match: ["Touch home glyph"], color: t.ink },
    ];
  },
  android(body) {
    const t = tones(body);
    return [
      // One moulded shell is the back and the sides. The glass face, with the
      // three key glyphs printed under it, is black on a phone of every colour.
      { match: ["Phone shell"], color: body },
      { match: ["Phone side keys"], color: t.button },
      { match: ["Phone lens ring", "Phone speaker slot"], color: t.well },
    ];
  },
};

/** Each catalog skin's body colour. `null` is the model as authored. */
const SKINS: Record<DeviceId, Record<string, string | null>> = {
  psp: {
    "piano-black": "#0d0d10",
    "ceramic-white": "#eeeae2",
    "felicia-blue": "#6f9fe8",
    "rose-pink": "#ff7fb0",
    mint: "#8be0c4",
    champagne: "#d9c08a",
  },
  vita: {
    black: null,
    "glacier-white": "#eef0f2",
    "aqua-blue": "#3bbfe0",
    lime: "#b8e04a",
    "neon-orange": "#ff8a3c",
    sakura: "#ff9cc2",
  },
  "3ds": {
    "pearl-white": null,
    black: "#232428",
    aqua: "#35c2d6",
    "flame-red": "#e5383b",
    grape: "#8f6fe0",
    sunny: "#ffd23f",
  },
  "ipod-touch": {
    black: null,
    white: "#f2f2f0",
    pink: "#ff6f9f",
    sky: "#4fc3f0",
    lemon: "#ffd84a",
    violet: "#a98bff",
  },
  android: {
    graphite: null,
    chalk: "#eeeeea",
    coral: "#ff6f61",
    leaf: "#6fcf5a",
    cyan: "#2fc4dc",
    sunflower: "#ffc93c",
  },
};

/** Skin ids this presenter can draw for a device. The catalog must not list others. */
export function knownSkins(device: DeviceId): string[] {
  return Object.keys(SKINS[device]);
}

/**
 * Paint a device. `skin` is a catalog skin id, or CUSTOM_SKIN with `custom`
 * as the body colour.
 */
export function applySkin(device: DeviceId, skin: string, materials: THREE.MeshStandardMaterial[], custom?: string | null): void {
  let body: string | null;
  if (skin === CUSTOM_SKIN) {
    body = parseColor(custom);
    if (!body) throw new Error(`the ${device}'s own colour is not a #rrggbb colour`);
  } else {
    if (!(skin in SKINS[device])) throw new Error(`no skin "${skin}" for ${device}`);
    body = SKINS[device][skin]!;
  }
  const rules = body ? PAINTERS[device](body) : [];
  for (const material of materials) {
    const authored = material.userData.authored as THREE.Color | undefined;
    if (authored) material.color.copy(authored);
    if (material.userData.authoredRoughness === undefined) {
      material.userData.authoredRoughness = material.roughness;
      material.userData.authoredMetalness = material.metalness;
    }
    material.roughness = material.userData.authoredRoughness;
    material.metalness = material.userData.authoredMetalness;
    const rule = rules.find((candidate) => candidate.match.some((prefix) => material.name.startsWith(prefix)));
    if (!rule) continue;
    material.color.set(rule.color);
    if (rule.roughness !== undefined) material.roughness = rule.roughness;
    if (rule.metalness !== undefined) material.metalness = rule.metalness;
  }
}
